import React, { useEffect, useRef } from "react";
import * as THREE from "three";

interface BookCanvasProps {
  threeObjectsRef: React.MutableRefObject<{
    bookGroup: THREE.Group | null;
    frontCover: THREE.Group | null;
    backCover: THREE.Group | null;
    pagesGroup: THREE.Group | null;
    singlePages: THREE.Group[];
    moonMesh: THREE.Mesh | null;
    pointLight: THREE.PointLight | null;
    starField: THREE.Points | null;
  }>;
}

export const BookCanvas: React.FC<BookCanvasProps> = ({ threeObjectsRef }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // --- 1. SETUP SCENE & CAMERA ---
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    const scene = new THREE.Scene();

    // Add atmospheric fog for cinematic depth
    scene.fog = new THREE.FogExp2("#0a0915", 0.08);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 8);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // --- 2. LIGHTING ---
    const ambientLight = new THREE.AmbientLight("#1e1b4b", 0.6);
    scene.add(ambientLight);

    const moonLight = new THREE.DirectionalLight("#dcd6f7", 2.2);
    moonLight.position.set(5, 8, -5);
    moonLight.castShadow = true;
    moonLight.shadow.mapSize.width = 1024;
    moonLight.shadow.mapSize.height = 1024;
    moonLight.shadow.bias = -0.001;
    scene.add(moonLight);

    // Glowing point light representing AI presence
    const pointLight = new THREE.PointLight("#fcf6bd", 3, 15);
    pointLight.position.set(0, 0, 1);
    scene.add(pointLight);

    // --- 3. PROCEDURAL TEXTURES ---
    // A. Moon Texture
    const createMoonTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      // Base texture
      const grad = ctx.createRadialGradient(256, 256, 0, 256, 256, 256);
      grad.addColorStop(0, "#eceef7");
      grad.addColorStop(0.8, "#dcd6f7");
      grad.addColorStop(1, "#b5b0d3");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 512, 512);

      // Craters
      ctx.fillStyle = "rgba(161, 140, 209, 0.25)";
      for (let i = 0; i < 40; i++) {
        const x = Math.random() * 512;
        const y = Math.random() * 512;
        const r = 5 + Math.random() * 25;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();

        // Shading for crater depth
        ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x + r * 0.1, y + r * 0.1, r * 0.9, Math.PI * 0.75, Math.PI * 1.75);
        ctx.stroke();
      }

      return new THREE.CanvasTexture(canvas);
    };

    // B. Leather / Gold book texture
    const createCoverTexture = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      // Rich Deep Blue/Violet leather texture
      ctx.fillStyle = "#161233";
      ctx.fillRect(0, 0, 512, 512);

      // Procedural leather bumps
      ctx.fillStyle = "rgba(10, 8, 26, 0.15)";
      for (let i = 0; i < 3000; i++) {
        const x = Math.random() * 512;
        const y = Math.random() * 512;
        ctx.fillRect(x, y, 1.5, 1.5);
      }

      // Elegant gold border lines
      ctx.strokeStyle = "#fcf6bd";
      ctx.lineWidth = 6;
      ctx.strokeRect(30, 30, 452, 452);
      ctx.lineWidth = 2;
      ctx.strokeRect(40, 40, 432, 432);

      // Central gold crescent moon design
      ctx.fillStyle = "#fcf6bd";
      ctx.beginPath();
      ctx.arc(256, 256, 70, 0, Math.PI * 2);
      ctx.fill();

      // Cut out to make a crescent moon
      ctx.fillStyle = "#161233";
      ctx.beginPath();
      ctx.arc(280, 240, 68, 0, Math.PI * 2);
      ctx.fill();

      // Gold stars around moon
      ctx.fillStyle = "#fcf6bd";
      const drawStar = (
        cx: number,
        cy: number,
        spikes: number,
        outerRadius: number,
        innerRadius: number
      ) => {
        let rot = (Math.PI / 2) * 3;
        let x = cx;
        let y = cy;
        const step = Math.PI / spikes;

        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);
        for (let i = 0; i < spikes; i++) {
          x = cx + Math.cos(rot) * outerRadius;
          y = cy + Math.sin(rot) * outerRadius;
          ctx.lineTo(x, y);
          rot += step;

          x = cx + Math.cos(rot) * innerRadius;
          y = cy + Math.sin(rot) * innerRadius;
          ctx.lineTo(x, y);
          rot += step;
        }
        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();
        ctx.fill();
      };

      drawStar(210, 180, 4, 12, 5);
      drawStar(310, 320, 4, 10, 4);
      drawStar(180, 280, 4, 8, 3);

      return new THREE.CanvasTexture(canvas);
    };

    const moonTex = createMoonTexture();
    const coverTex = createCoverTexture();

    // --- 4. OBJECTS CREATION ---

    // A. THE MOON
    const moonGeom = new THREE.SphereGeometry(1.5, 64, 64);
    const moonMat = new THREE.MeshStandardMaterial({
      map: moonTex,
      roughness: 0.9,
      metalness: 0.1,
      bumpMap: moonTex ?? undefined,
      bumpScale: 0.04,
    });
    const moonMesh = new THREE.Mesh(moonGeom, moonMat);
    moonMesh.position.set(0, 0.5, -6); // Positioned deeply in background
    scene.add(moonMesh);

    // B. PROCEDURAL BOOK (Folds open around hinges!)
    const bookGroup = new THREE.Group();
    scene.add(bookGroup);

    // Book Dimensions
    const bWidth = 1.4;
    const bHeight = 2.0;
    const bThickness = 0.12;

    // Material definitions
    const coverMaterial = new THREE.MeshStandardMaterial({
      map: coverTex ?? undefined,
      roughness: 0.4,
      metalness: 0.3,
    });

    const spineMaterial = new THREE.MeshStandardMaterial({
      color: "#161233",
      roughness: 0.5,
      metalness: 0.2,
    });

    const pageMaterial = new THREE.MeshStandardMaterial({
      color: "#f6f3eb", // Warm cream paper
      roughness: 0.8,
      metalness: 0.05,
    });

    // 1. Central Spine
    const spineGeom = new THREE.BoxGeometry(0.12, bHeight, bThickness);
    const spineMesh = new THREE.Mesh(spineGeom, spineMaterial);
    spineMesh.position.set(0, 0, 0);
    bookGroup.add(spineMesh);

    // 2. Front Cover (Left cover in standard closed coordinate)
    // Pivot is at the right edge of front cover (touching the spine)
    const frontCoverPivot = new THREE.Group();
    frontCoverPivot.position.set(-0.06, 0, 0); // At edge of spine
    bookGroup.add(frontCoverPivot);

    const coverGeom = new THREE.BoxGeometry(bWidth, bHeight, 0.05);
    // Align cover mesh so its hinge is at x=0 in frontCoverPivot
    const frontCoverMesh = new THREE.Mesh(coverGeom, coverMaterial);
    frontCoverMesh.position.set(-bWidth / 2, 0, 0.025);
    frontCoverMesh.castShadow = true;
    frontCoverPivot.add(frontCoverMesh);

    // 3. Back Cover
    const backCoverPivot = new THREE.Group();
    backCoverPivot.position.set(0.06, 0, 0);
    bookGroup.add(backCoverPivot);

    const backCoverMesh = new THREE.Mesh(coverGeom, coverMaterial);
    // Mirror of cover map for back cover
    backCoverMesh.position.set(bWidth / 2, 0, 0.025);
    backCoverMesh.castShadow = true;
    backCoverPivot.add(backCoverMesh);

    // 4. Pages block (left and right page blocks)
    const pagesGroup = new THREE.Group();
    pagesGroup.position.set(0, 0, 0.01);
    bookGroup.add(pagesGroup);

    // Dynamic paper sheets for turning page animation
    const singlePages: THREE.Group[] = [];
    const numSheets = 6;

    const pWidth = bWidth - 0.06;
    const pHeight = bHeight - 0.08;
    const pThickness = 0.005;
    const pageGeom = new THREE.BoxGeometry(pWidth, pHeight, pThickness);

    for (let i = 0; i < numSheets; i++) {
      const pagePivot = new THREE.Group();
      pagePivot.position.set(0.04, 0, 0.015 + i * 0.002);
      pagesGroup.add(pagePivot);

      const pageMesh = new THREE.Mesh(pageGeom, pageMaterial);
      pageMesh.position.set(pWidth / 2, 0, 0);
      pageMesh.castShadow = true;
      pagePivot.add(pageMesh);

      singlePages.push(pagePivot);
    }

    // Static left background pages block
    const leftPagesGeom = new THREE.BoxGeometry(
      bWidth - 0.06,
      bHeight - 0.06,
      bThickness - 0.04
    );
    const leftPagesMesh = new THREE.Mesh(leftPagesGeom, pageMaterial);
    leftPagesMesh.position.set(-(bWidth - 0.06) / 2 - 0.05, 0, 0.015);
    pagesGroup.add(leftPagesMesh);

    // Set initial closed states
    frontCoverPivot.rotation.y = 0;
    backCoverPivot.rotation.y = 0;

    // Hide pages initially inside the closed covers
    pagesGroup.visible = false;

    // Scale bookGroup
    bookGroup.scale.set(1.5, 1.5, 1.5);
    bookGroup.position.set(0, 0, -1);
    bookGroup.rotation.set(0.3, -0.4, 0.15); // Cozy 3D angle

    // C. STARFIELD / DUST
    const starCount = 1200;
    const starGeom = new THREE.BufferGeometry();
    const starPositions = new Float32Array(starCount * 3);
    const starColors = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount * 3; i += 3) {
      const r = 2 + Math.random() * 12;
      const theta = Math.random() * Math.PI * 2;
      const z = (Math.random() - 0.5) * 12;

      starPositions[i] = Math.cos(theta) * r;
      starPositions[i + 1] = Math.sin(theta) * r;
      starPositions[i + 2] = z;

      // Soft shades of lavender, gold and light blue
      const rColor = Math.random();
      if (rColor > 0.6) {
        // Gold star
        starColors[i] = 1.0;
        starColors[i + 1] = 0.96;
        starColors[i + 2] = 0.74;
      } else if (rColor > 0.3) {
        // Lavender star
        starColors[i] = 0.86;
        starColors[i + 1] = 0.84;
        starColors[i + 2] = 0.97;
      } else {
        // Soft blue star
        starColors[i] = 0.63;
        starColors[i + 1] = 0.81;
        starColors[i + 2] = 0.95;
      }
    }

    starGeom.setAttribute("position", new THREE.BufferAttribute(starPositions, 3));
    starGeom.setAttribute("color", new THREE.BufferAttribute(starColors, 3));

    // Custom circle texture for soft round particles
    const createCircleTexture = () => {
      const cCanvas = document.createElement("canvas");
      cCanvas.width = 16;
      cCanvas.height = 16;
      const cCtx = cCanvas.getContext("2d");
      if (cCtx) {
        const grad = cCtx.createRadialGradient(8, 8, 0, 8, 8, 8);
        grad.addColorStop(0, "rgba(255, 255, 255, 1)");
        grad.addColorStop(0.5, "rgba(255, 255, 255, 0.4)");
        grad.addColorStop(1, "rgba(255, 255, 255, 0)");
        cCtx.fillStyle = grad;
        cCtx.fillRect(0, 0, 16, 16);
      }
      return new THREE.CanvasTexture(cCanvas);
    };

    const starMaterial = new THREE.PointsMaterial({
      size: 0.05,
      map: createCircleTexture(),
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const starField = new THREE.Points(starGeom, starMaterial);
    scene.add(starField);

    // --- 5. EXPOSE REFERENCES TO PARENT ---
    threeObjectsRef.current = {
      bookGroup,
      frontCover: frontCoverPivot,
      backCover: backCoverPivot,
      pagesGroup,
      singlePages,
      moonMesh,
      pointLight,
      starField,
    };

    // --- 6. RENDER LOOP ---
    let animationFrameId: number;
    const clock = new THREE.Clock();

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      const elapsedTime = clock.getElapsedTime();

      // Subtle float animations (Idle state)
      if (moonMesh) {
        moonMesh.rotation.y = elapsedTime * 0.02;
      }

      if (starField) {
        starField.rotation.y = elapsedTime * 0.01;
        // Sparkle effect
        const positions = starField.geometry.attributes.position.array as Float32Array;
        for (let i = 1; i < positions.length; i += 3) {
          positions[i] += Math.sin(elapsedTime + i) * 0.001;
        }
        starField.geometry.attributes.position.needsUpdate = true;
      }

      // Slightly pulse point light
      if (pointLight) {
        pointLight.intensity = 1.5 + Math.sin(elapsedTime * 3) * 0.5;
      }

      renderer.render(scene, camera);
    };

    animate();

    // --- 7. RESIZE HANDLING ---
    const handleResize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;

      camera.aspect = w / h;
      camera.updateProjectionMatrix();

      renderer.setSize(w, h);
    };

    window.addEventListener("resize", handleResize);

    // --- 8. CLEANUP ---
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
      if (containerRef.current && renderer.domElement) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        containerRef.current.removeChild(renderer.domElement);
      }

      // Dispose resources
      moonGeom.dispose();
      moonMat.dispose();
      spineGeom.dispose();
      spineMaterial.dispose();
      coverGeom.dispose();
      coverMaterial.dispose();
      pageGeom.dispose();
      pageMaterial.dispose();
      leftPagesGeom.dispose();
      starGeom.dispose();
      starMaterial.dispose();
      if (moonTex) moonTex.dispose();
      if (coverTex) coverTex.dispose();
    };
  }, [threeObjectsRef]);

  return (
    <div
      id="three-root-container"
      ref={containerRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
    />
  );
};
